import React, { useState, useEffect } from "react";
import { apiFetch } from "../services/api";

const EmailVerification = ({ email, token, onVerificationComplete }) => {
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("Verifying your email address...");
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    let verificationCompleted = false;

    const verifyEmail = async () => {
      // Prevent multiple verification attempts
      if (verificationCompleted) {
        return;
      }

      if (!email || !token) {
        if (isMounted) {
          setStatus("error");
          setMessage("Invalid verification link");
          setError("Missing email or token in verification link");
        }
        return;
      }

      try {
        if (isMounted) {
          setStatus("verifying");
          setMessage("Verifying your email address...");
        }

        const response = await apiFetch("/api/auth/verify-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, token }),
        });

        if (response.ok) {
          const data = await response.json();
          verificationCompleted = true;

          if (isMounted) {
            setStatus("success");
            if (data.message && data.message.includes("already verified")) {
              setMessage("Your email address is already verified!");
            } else {
              setMessage("Email verified successfully!");
            }

            // Call completion callback if provided (only once)
            if (onVerificationComplete) {
              setTimeout(() => {
                if (isMounted) {
                  onVerificationComplete();
                }
              }, 2000);
            }
          }
        } else {
          const errorData = await response.json();
          if (isMounted) {
            setStatus("error");
            setMessage("Email verification failed");
            setError(
              errorData.detail ||
                "The verification link may have expired or is invalid."
            );
          }
        }
      } catch (err) {
        if (isMounted) {
          setStatus("error");
          setMessage("Email verification failed");
          setError(
            "Network error. Please try again or request a new verification email."
          );
        }
      }
    };

    verifyEmail();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [email, token]); // Removed onVerificationComplete from dependencies to prevent re-runs

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="text-center">
            {status === "verifying" && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500"></div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Verifying Email
                </h2>
                <p className="text-gray-300">{message}</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="flex justify-center mb-4">
                  <svg
                    className="h-16 w-16 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">
                  Email Verified!
                </h2>
                <p className="text-gray-300 mb-6">{message}</p>
                <p className="text-gray-400 text-sm mb-6">
                  You can now log in to your account.
                </p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    // Clear URL parameters immediately to prevent re-triggering
                    window.history.replaceState(
                      {},
                      document.title,
                      window.location.pathname
                    );
                    // Clear sessionStorage as well
                    sessionStorage.removeItem("pendingResetEmail");
                    sessionStorage.removeItem("pendingResetToken");
                    sessionStorage.removeItem("pendingResetType");
                    // Always call the callback if provided, it handles navigation
                    if (onVerificationComplete) {
                      onVerificationComplete();
                    } else {
                      // Fallback: redirect to home which should show login
                      window.location.href = "/";
                    }
                  }}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                >
                  Go to Login
                </button>
              </>
            )}

            {status === "error" && (
              <>
                <div className="flex justify-center mb-4">
                  <svg
                    className="h-16 w-16 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">
                  Verification Failed
                </h2>
                <p className="text-gray-300 mb-4">{message}</p>
                {error && <p className="text-red-300 text-sm mb-6">{error}</p>}
                <div className="space-y-3">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      // Clear URL parameters immediately to prevent re-triggering
                      window.history.replaceState(
                        {},
                        document.title,
                        window.location.pathname
                      );
                      // Clear sessionStorage as well
                      sessionStorage.removeItem("pendingResetEmail");
                      sessionStorage.removeItem("pendingResetToken");
                      sessionStorage.removeItem("pendingResetType");
                      if (onVerificationComplete) {
                        onVerificationComplete();
                      } else {
                        window.location.href = "/";
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  >
                    Go to Login
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const response = await apiFetch(
                          "/api/auth/resend-verification",
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ email }),
                          }
                        );
                        if (response.ok) {
                          setMessage(
                            "A new verification email has been sent. Please check your inbox."
                          );
                          setError("");
                        } else {
                          setError("Failed to resend verification email.");
                        }
                      } catch (err) {
                        setError("Failed to resend verification email.");
                      }
                    }}
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Resend Verification Email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
